import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../api'
import Button from '../components/Button'

interface ScoreData {
  wellbeing_score: number
  burnout_risk: 'low' | 'medium' | 'high'
  entries_count: number
  avg_sentiment: number
  period_start: string
}

interface FinancialStress {
  stress_score: number | null
  logged_this_week: boolean
}

interface EnergyBudget {
  energy_percentage: number
  energy_level: 'high' | 'moderate' | 'low' | 'depleted'
  message: string
  suggestions: string[]
  factors: string[]
}

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [score, setScore] = useState<ScoreData | null>(null)
  const [loading, setLoading] = useState(true)
  const [dailyPrompt, setDailyPrompt] = useState('')
  const [financialStress, setFinancialStress] = useState<FinancialStress>({
    stress_score: null,
    logged_this_week: false,
  })
  const [selectedStress, setSelectedStress] = useState<number | null>(null)
  const [savingStress, setSavingStress] = useState(false)
  const [stressSaved, setStressSaved] = useState(false)
  const [energyBudget, setEnergyBudget] = useState<EnergyBudget | null>(null)

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const [scoreRes, promptRes, financialRes, energyRes] = await Promise.all([
        api.get('/scores/latest'),
        api.get('/prompts/daily').catch(() => ({ data: { prompt: '' } })),
        api.get('/financial/stress').catch(() => ({
          data: { stress_score: null, logged_this_week: false }
        })),
        api.get('/scores/energy').catch(() => ({ data: null })),
      ])

      if (scoreRes.data.wellbeing_score !== undefined) {
        setScore(scoreRes.data)
      }

      setDailyPrompt(promptRes.data.prompt || '')
      setFinancialStress(financialRes.data)

      if (financialRes.data.stress_score) {
        setSelectedStress(financialRes.data.stress_score)
      }

      if (energyRes.data) {
        setEnergyBudget(energyRes.data)
      }

    } catch (err) {
      console.error('Failed to load dashboard', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveFinancialStress = async () => {
    if (!selectedStress) return
    setSavingStress(true)
    try {
      await api.post('/financial/stress', { stress_score: selectedStress })
      setFinancialStress({ stress_score: selectedStress, logged_this_week: true })
      setStressSaved(true)
      setTimeout(() => setStressSaved(false), 3000)
      const energyRes = await api.get('/scores/energy')
      setEnergyBudget(energyRes.data)
    } catch (err) {
      console.error('Failed to save financial stress', err)
    } finally {
      setSavingStress(false)
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'var(--green)'
      case 'medium': return 'var(--amber)'
      case 'high': return 'var(--rose)'
      default: return 'var(--muted)'
    }
  }

  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'low': return 'Looking balanced'
      case 'medium': return 'Worth watching'
      case 'high': return 'Needs attention'
      default: return 'Not yet calculated'
    }
  }

  const getEnergyColor = (level: string) => {
    switch (level) {
      case 'high': return '#2D6A4F'
      case 'moderate': return '#C8963E'
      case 'low': return '#B45309'
      case 'depleted': return '#9F1239'
      default: return '#78716C'
    }
  }

  const getEnergyLabel = (level: string) => {
    switch (level) {
      case 'high': return 'High energy'
      case 'moderate': return 'Moderate energy'
      case 'low': return 'Low energy'
      case 'depleted': return 'Running on empty'
      default: return 'Calculating...'
    }
  }

  const stressLabels: Record<number, string> = {
    1: 'None', 2: 'A little', 3: 'Moderate',
    4: 'Quite a lot', 5: 'Severe',
  }

  const stressColors: Record<number, string> = {
    1: '#2D6A4F', 2: '#6B8F5E', 3: '#C8963E',
    4: '#B45309', 5: '#9F1239',
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div style={{
          width: '28px', height: '28px',
          border: '3px solid var(--warm-border)',
          borderTopColor: 'var(--gold)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  return (
    <div className="animate-fadeUp">

      {/* Greeting */}
      <h2 style={{
        fontFamily: 'var(--font-display)',
        marginBottom: '4px',
        fontSize: 'clamp(20px, 4vw, 28px)',
      }}>
        Good {greeting}, {user?.display_name || 'there'}.
      </h2>
      <p style={{ color: 'var(--muted)', marginBottom: '32px', fontSize: '14px' }}>
        Here's how things are looking for you today.
      </p>

      {/* Energy Budget */}
      {energyBudget && (
        <div style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          border: '1px solid var(--cream-dark)',
          marginBottom: '24px',
          borderLeft: '4px solid ' + getEnergyColor(energyBudget.energy_level),
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            flexWrap: 'wrap' as const, gap: '8px',
          }}>
            <div>
              <p style={{
                fontSize: '11px', letterSpacing: '0.1em',
                textTransform: 'uppercase' as const,
                color: 'var(--muted)', marginBottom: '4px',
              }}>
                Your Energy Budget Today
              </p>
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(16px, 3vw, 20px)',
                color: getEnergyColor(energyBudget.energy_level),
              }}>
                {getEnergyLabel(energyBudget.energy_level)}
              </h3>
            </div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(36px, 8vw, 48px)',
              fontWeight: 700, lineHeight: 1,
              color: getEnergyColor(energyBudget.energy_level),
            }}>
              {energyBudget.energy_percentage}%
            </div>
          </div>

          <div style={{
            height: '10px', background: 'var(--cream-dark)',
            borderRadius: 'var(--radius-full)', overflow: 'hidden',
            marginBottom: '16px',
          }}>
            <div style={{
              height: '100%',
              width: energyBudget.energy_percentage + '%',
              background: getEnergyColor(energyBudget.energy_level),
              borderRadius: 'var(--radius-full)',
              transition: 'width 1s ease',
            }} />
          </div>

          <p style={{
            fontSize: '14px', color: 'var(--ink-soft)',
            lineHeight: 1.7, marginBottom: '16px',
          }}>
            {energyBudget.message}
          </p>

          {energyBudget.suggestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {energyBudget.suggestions.map((suggestion, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  fontSize: '13px', color: 'var(--ink-soft)',
                }}>
                  <span style={{
                    color: getEnergyColor(energyBudget.energy_level),
                    fontWeight: 600, flexShrink: 0,
                  }}>→</span>
                  {suggestion}
                </div>
              ))}
            </div>
          )}

          {energyBudget.factors.length > 0 && (
            <p style={{
              fontSize: '11px', color: 'var(--warm-border)',
              marginTop: '16px', fontStyle: 'italic',
            }}>
              Based on: {energyBudget.factors.join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* Wellbeing Score + Daily Prompt */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '20px',
        marginBottom: '20px',
      }}>

        {/* Wellbeing Score Card */}
        <div style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-lg)',
          padding: '28px',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--cream-dark)',
        }}>
          {score ? (
            <>
              <p style={{
                fontSize: '11px', letterSpacing: '0.08em',
                textTransform: 'uppercase' as const,
                color: 'var(--muted)', marginBottom: '16px',
              }}>
                Your Wellbeing Score
              </p>
              <div style={{
                display: 'flex', alignItems: 'baseline',
                gap: '12px', marginBottom: '8px',
              }}>
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(48px, 10vw, 64px)',
                  fontWeight: 700, color: 'var(--navy)', lineHeight: 1,
                }}>
                  {Math.round(score.wellbeing_score)}
                </span>
                <span style={{ fontSize: '18px', color: 'var(--muted)' }}>/100</span>
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '6px 14px', borderRadius: 'var(--radius-full)',
                background: getRiskColor(score.burnout_risk) + '15',
                marginBottom: '20px',
              }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: getRiskColor(score.burnout_risk),
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: '13px', fontWeight: 500,
                  color: getRiskColor(score.burnout_risk),
                }}>
                  {getRiskLabel(score.burnout_risk)}
                </span>
              </div>
              <div style={{
                height: '8px', background: 'var(--cream-dark)',
                borderRadius: 'var(--radius-full)', overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', width: score.wellbeing_score + '%',
                  background: getRiskColor(score.burnout_risk),
                  borderRadius: 'var(--radius-full)',
                  transition: 'width 0.6s ease',
                }} />
              </div>
              <p style={{
                fontSize: '12px', color: 'var(--muted)', marginTop: '12px',
              }}>
                Based on {score.entries_count} entries this period
              </p>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <p style={{
                fontFamily: 'var(--font-display)',
                fontSize: '18px', marginBottom: '8px',
              }}>
                No score yet
              </p>
              <p style={{
                fontSize: '14px', color: 'var(--muted)', marginBottom: '20px',
                lineHeight: 1.6,
              }}>
                Write a few entries this week and your first
                wellbeing score will appear here.
              </p>
              <Button size="sm" onClick={() => navigate('/journal')}>
                Write your first entry
              </Button>
            </div>
          )}
        </div>

        {/* Daily Prompt Card */}
        <div style={{
          background: 'var(--navy)',
          borderRadius: 'var(--radius-lg)',
          padding: '28px', color: 'var(--cream)',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: '200px',
        }}>
          <div>
            <p style={{
              fontSize: '11px', letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              color: 'var(--gold)', marginBottom: '16px',
            }}>
              Today's reflection
            </p>
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(15px, 2.5vw, 18px)',
              fontStyle: 'italic', lineHeight: 1.6, color: 'var(--cream)',
            }}>
              {dailyPrompt || 'What is on your mind today?'}
            </p>
          </div>
          <button
            onClick={() => navigate('/journal')}
            style={{
              marginTop: '24px', padding: '12px 20px',
              background: 'transparent',
              border: '1.5px solid rgba(248,244,238,0.3)',
              borderRadius: 'var(--radius-md)', color: 'var(--cream)',
              fontSize: '14px', cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              transition: 'all var(--transition-fast)',
              textAlign: 'left' as const,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--gold)'
              e.currentTarget.style.color = 'var(--gold)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(248,244,238,0.3)'
              e.currentTarget.style.color = 'var(--cream)'
            }}
          >
            Start writing →
          </button>
        </div>
      </div>

      {/* Financial Stress Slider */}
      <div style={{
        background: 'var(--white)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        border: '1px solid var(--cream-dark)',
        marginBottom: '20px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '8px', gap: '12px',
          flexWrap: 'wrap' as const,
        }}>
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '16px', marginBottom: '4px',
            }}>
              Financial Stress Check-in
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)' }}>
              How much has financial stress affected you this week?
            </p>
          </div>
          {financialStress.logged_this_week && (
            <span style={{
              fontSize: '11px', color: 'var(--green)',
              background: 'var(--green-light)',
              padding: '4px 10px',
              borderRadius: 'var(--radius-full)',
              fontWeight: 500, flexShrink: 0,
            }}>
              Logged this week
            </span>
          )}
        </div>

        <div style={{
          display: 'flex', gap: '8px',
          marginTop: '16px', marginBottom: '16px',
          flexWrap: 'wrap' as const,
        }}>
          {[1, 2, 3, 4, 5].map(level => (
            <button
              key={level}
              onClick={() => setSelectedStress(level)}
              style={{
                flex: 1, minWidth: '56px', padding: '10px 4px',
                borderRadius: 'var(--radius-md)', cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: '12px',
                fontWeight: selectedStress === level ? 600 : 400,
                transition: 'all var(--transition-fast)',
                border: selectedStress === level
                  ? '2px solid ' + stressColors[level]
                  : '1.5px solid var(--warm-border)',
                background: selectedStress === level
                  ? stressColors[level] + '15'
                  : 'var(--cream)',
                color: selectedStress === level
                  ? stressColors[level]
                  : 'var(--muted)',
              }}
            >
              <div style={{ fontSize: '18px', marginBottom: '4px' }}>
                {level === 1 ? '😌' : level === 2 ? '🙂' : level === 3 ? '😐' : level === 4 ? '😟' : '😰'}
              </div>
              <div>{stressLabels[level]}</div>
            </button>
          ))}
        </div>

        {selectedStress && (
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: '12px', flexWrap: 'wrap' as const,
          }}>
            <Button
              size="sm"
              loading={savingStress}
              onClick={handleSaveFinancialStress}
              variant={financialStress.logged_this_week ? 'secondary' : 'primary'}
            >
              {financialStress.logged_this_week ? 'Update score' : 'Save score'}
            </Button>
            {stressSaved && (
              <span style={{ fontSize: '13px', color: 'var(--green)' }}>
                ✓ Saved — this factors into your burnout score
              </span>
            )}
          </div>
        )}

        {!selectedStress && !financialStress.logged_this_week && (
          <p style={{
            fontSize: '12px', color: 'var(--warm-border)', marginTop: '8px',
            lineHeight: 1.6,
          }}>
            Financial stress is one of the top burnout triggers.
            Logging it helps Velumio give you a more accurate picture.
          </p>
        )}
      </div>

      {/* Quick Action Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
      }}>
        <div
          onClick={() => navigate('/letter')}
          style={{
            background: 'var(--white)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            border: '1px solid var(--cream-dark)',
            cursor: 'pointer',
            transition: 'transform var(--transition-fast)',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          <p style={{ fontSize: '24px', marginBottom: '8px' }}>✉</p>
          <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--ink)' }}>
            Your weekly letter
          </p>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>
            A personal note written just for you
          </p>
        </div>

        <div
          onClick={() => navigate('/journal')}
          style={{
            background: 'var(--white)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px',
            border: '1px solid var(--cream-dark)',
            cursor: 'pointer',
            transition: 'transform var(--transition-fast)',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          <p style={{ fontSize: '24px', marginBottom: '8px' }}>✦</p>
          <p style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--ink)' }}>
            Your journal
          </p>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>
            Write, speak, or Walk & Talk
          </p>
        </div>
      </div>

    </div>
  )
}